if (!customElements.get('gallery-variant-thumbnail')) {
  customElements.define(
    'gallery-variant-thumbnail',
    class GalleryVariantThumbnail extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.selectedClass = 'thumbnail-list_item--variant-selected';
        this.mediaGallery = this.closest('media-gallery');
        this.lockMainGalleryImage();
        this.captureModalOrder();
        // Kindle Case products use a settings-driven synthetic variant slide
        // instead of a real variant image; that path is mutually exclusive with
        // Dawn's featured_media-based variant logic. The slide is always present
        // when the feature is configured (its server-rendered state is correct on
        // load), so we only need to recompute the slider counter here.
        if (this.hasKindleVariant()) {
          this.refreshGallerySlider();
        } else {
          this.markActiveVariantMedia(this.dataset.selectedMediaId);
        }
        this.subscribeWhenReady();
      }

      hasKindleVariant() {
        return !!document.getElementById(`Slide-${this.sectionId}-kindle-variant`);
      }

      kindleVariantElementIds() {
        return [
          `Slide-${this.sectionId}-kindle-variant`,
          `Slide-${this.sectionId}-kindle-variant-duplicate`,
          `Slide-Thumbnails-${this.sectionId}-kindle-variant`,
          `Slide-Thumbnails-${this.sectionId}-kindle-variant-duplicate`,
          `KindleVariantModalImage-${this.sectionId}`,
        ];
      }

      // Show/hide a synthetic variant element for the selected model. The gallery
      // slide and thumbnail rely on their variant classes (hidden by CSS without
      // them); the modal <img> additionally needs an inline display toggle because
      // it isn't hidden by CSS on mobile.
      setKindleActive(element, active) {
        if (!element) return;
        if (element.id.indexOf('Slide-Thumbnails') === 0) {
          element.classList.toggle(this.selectedClass, active);
        } else if (element.tagName === 'IMG') {
          element.classList.toggle('is-variant-active', active);
          element.style.display = active ? '' : 'none';
        } else {
          element.classList.toggle('is-variant-active', active);
        }
      }

      // With hide_variants on, every variant image is hidden except the selected
      // one. Dawn reveals it by hoisting it to :first-child; we instead flag the
      // selected variant's media so it shows in its natural gallery position — in
      // the modal (desktop) and directly in the slider on mobile, where there are
      // no thumbnails. See section-main-product.css.
      markActiveVariantMedia(mediaId) {
        // Modal media use the bare media id; gallery slides are section-prefixed.
        this.toggleVariantActive(this.modalContent, mediaId);
        this.toggleVariantActive(this.mediaGallery, mediaId ? `${this.sectionId}-${mediaId}` : null);
        this.refreshGallerySlider();
      }

      toggleVariantActive(scope, targetId) {
        if (!scope) return;
        scope
          .querySelectorAll('.is-variant-active')
          .forEach((item) => item.classList.remove('is-variant-active'));
        if (!targetId) return;
        const target = scope.querySelector(`[data-media-id="${targetId}"]`);
        if (target) target.classList.add('is-variant-active');
      }

      // Showing/hiding a variant slide changes how many slides are visible, so the
      // slider counter (e.g. "1/7") and paging must be recomputed. resetPages()
      // re-filters slides by visibility and refreshes the counter.
      refreshGallerySlider() {
        const viewer = this.mediaGallery && this.mediaGallery.querySelector('[id^="GalleryViewer"]');
        if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();
      }

      // Snapshot the natural order of the lightbox modal media. Dawn's
      // variant-selects (updateMedia) prepends the variant media to the modal on
      // every variant change, which breaks the gallery's order. We restore this
      // snapshot after each variant change so the modal stays in sync.
      captureModalOrder() {
        this.modalContent = document.querySelector(
          `#ProductModal-${this.sectionId} .product-media-modal__content`
        );
        if (!this.modalContent) return;
        this.modalMediaOrder = Array.from(this.modalContent.children);
      }

      restoreModalOrder() {
        if (!this.modalContent || !this.modalMediaOrder) return;
        this.modalMediaOrder.forEach((media) => this.modalContent.appendChild(media));
      }

      // Keep the big gallery image pinned to the first media. Dawn's
      // variant-selects calls setActiveMedia(id, true) on variant change
      // (prepend === true) to swap the main image. We no-op only those calls so
      // the first image always stays selected, while user thumbnail clicks
      // (prepend === false) still swap the image normally.
      lockMainGalleryImage() {
        const gallery = this.mediaGallery;
        if (!gallery) return;

        customElements.whenDefined('media-gallery').then(() => {
          if (gallery.variantImageLocked) return;
          const originalSetActiveMedia = gallery.setActiveMedia;
          if (typeof originalSetActiveMedia !== 'function') return;

          gallery.variantImageLocked = true;
          gallery.setActiveMedia = function (mediaId, prepend) {
            if (prepend) return;
            return originalSetActiveMedia.call(this, mediaId, prepend);
          };
        });
      }

      // On variant change, snap the big gallery image back to the first media.
      // The lock above stops Dawn from swapping to the variant image, but if the
      // shopper manually opened a (variant) thumbnail beforehand, that stale image
      // would otherwise stay active. prepend === false runs the original logic.
      resetMainGalleryToFirst() {
        const gallery = this.mediaGallery;
        if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
        const viewer = gallery.querySelector('[id^="GalleryViewer"]');
        const firstMedia = viewer && viewer.querySelector('[data-media-id]');
        if (!firstMedia) return;
        gallery.setActiveMedia(firstMedia.dataset.mediaId, false);
      }

      subscribeWhenReady() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
          document.addEventListener('DOMContentLoaded', () => this.subscribeWhenReady(), { once: true });
          return;
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      // The per-model image is a theme-settings image rendered server-side as a
      // synthetic "variant" slide (gallery slide, thumbnail and modal slide), kept
      // always present so it can be revealed even when the page loaded on a model
      // with no image. Dawn re-fetches the section on every variant change, so we
      // copy the freshly rendered image + active state from that HTML — no extra
      // request, markup stays server-rendered.
      swapKindleVariantImages(html) {
        if (!html) return;

        // Swap only the <img> attributes, never the container innerHTML: the
        // thumbnail button's click handler is bound imperatively by
        // media-gallery.js and would be lost if we replaced the markup.
        const swapImage = (live, fresh) => {
          const liveImage = live.tagName === 'IMG' ? live : live.querySelector('img');
          const freshImage = fresh.tagName === 'IMG' ? fresh : fresh.querySelector('img');
          if (!liveImage || !freshImage) return;
          ['srcset', 'src', 'sizes', 'alt'].forEach((attr) => {
            liveImage.setAttribute(attr, freshImage.getAttribute(attr) || '');
          });
        };

        this.kindleVariantElementIds().forEach((id) => {
          const live = document.getElementById(id);
          if (!live) return;
          const fresh = html.getElementById(id);
          if (!fresh) {
            this.setKindleActive(live, false);
            return;
          }
          swapImage(live, fresh);
          this.setKindleActive(live, fresh.getAttribute('data-kindle-active') === 'true');
        });
      }

      onVariantChange(event) {
        if (event.data?.sectionId !== this.sectionId) return;

        this.restoreModalOrder();

        if (this.hasKindleVariant()) {
          this.swapKindleVariantImages(event.data.html);
          this.resetMainGalleryToFirst();
          this.refreshGallerySlider();
          return;
        }

        this.markActiveVariantMedia(event.data.variant?.featured_media?.id);
        this.resetMainGalleryToFirst();

        document
          .querySelectorAll(`.thumbnail-list_item--variant[data-target^="${this.sectionId}-"]`)
          .forEach((item) => item.classList.remove(this.selectedClass));

        const mediaId = event.data.variant?.featured_media?.id;
        if (!mediaId) return;

        const selected = document.querySelector(
          `.thumbnail-list_item--variant[data-target="${this.sectionId}-${mediaId}"]`
        );
        if (selected) selected.classList.add(this.selectedClass);
      }
    }
  );
}
